/**
 * logActivityShared.tsx — what the Log Activity sheet, its host and the pages
 * share (slice C1), kept out of the component files so each of those exports
 * components only (react-refresh): the activity type config, the host's
 * context and its hook.
 */
import { createContext, useContext } from 'react'
import { Mail, Phone, Users, FileText, CheckSquare } from 'lucide-react'
import type { SalesActivity, SalesContact, SalesDeal } from '../../types'
import type { SalesActivity as WireActivity } from '../../../../api/types'
import { activityType } from '../../../../theme/tokens'
import { useWriteAccess } from '../../../../lib/writeAccess'

// ── Type config ───────────────────────────────────────────────────────────────

export const TYPE_CONFIG: Record<SalesActivity['type'], { icon: React.ReactNode; color: string; bg: string; label: string; description: string }> = {
  email:   { icon: <Mail size={13} />,        color: activityType.email.color,   bg: activityType.email.bg,   label: 'Email',   description: 'Log an email sent or received' },
  call:    { icon: <Phone size={13} />,       color: activityType.call.color,    bg: activityType.call.bg,    label: 'Call',    description: 'Log a phone or video call' },
  meeting: { icon: <Users size={13} />,       color: activityType.meeting.color, bg: activityType.meeting.bg, label: 'Meeting', description: 'Log an in-person or virtual meeting' },
  note:    { icon: <FileText size={13} />,    color: activityType.note.color,    bg: activityType.note.bg,    label: 'Note',    description: 'Add a note about this contact or deal' },
  task:    { icon: <CheckSquare size={13} />, color: activityType.task.color,    bg: activityType.task.bg,    label: 'Task',    description: 'Create a follow-up task' },
}

export interface LogActivityPrefill {
  contact?: SalesContact | null
  deal?: SalesDeal | null
  /** The opener's own contacts read, when it has one (the Activities page);
   *  otherwise the host reads the first CONTACTS_CAP itself. */
  contacts?: SalesContact[]
  /** The type the sheet opens on (deal detail slice 2: the timeline's Call,
   *  Email and Meeting hand-off). Absent = the last type used, as before. */
  type?: SalesActivity['type']
}

/** Where focus returns when the sheet closes, resolved at CLOSE time. */
export type FocusReturn = () => HTMLElement | null

export interface LogActivityApi {
  open: (prefill: LogActivityPrefill, focusReturn: FocusReturn) => void
  /** Called with the SERVER's row once an activity exists (pending if the
   *  complete that followed failed). Returns the unsubscribe. */
  subscribe: (onCreated: (created: WireActivity) => void) => () => void
}

/** The bare key that opens the sheet (slice C2, Q1/Q3): compared by `key`,
 *  lower-cased, with no modifier at all. */
export const LOG_ACTIVITY_SHORTCUT = 'l'

export const LogActivityContext = createContext<LogActivityApi | null>(null)

/** The host's API, or null outside a host (a page rendered on its own) — and
 *  null for a caller whose role cannot create activities (records access,
 *  lib/writeAccess): every opener already hides itself on null, so a viewer
 *  is offered no Log activity entry point at all. */
export function useLogActivity(): LogActivityApi | null {
  const api = useContext(LogActivityContext)
  const { records } = useWriteAccess()
  return records ? api : null
}

